import * as maptilersdk from '@maptiler/sdk'
import { store } from '../../lib/store'
import { dateConvertion } from '../main-ui/common/commonfunctions'
import { expandLeftToolbar } from '../../action/left-toolbar-state-actions'

function haversineDistance([lng1, lat1], [lng2, lat2]) {
  const R = 6371000
  const dLat = (lat2 - lat1) * Math.PI / 180
  const dLng = (lng2 - lng1) * Math.PI / 180
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLng / 2) ** 2
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
}

function formatLength(meters, units) {
  if (units === 'imperial') return (meters / 1609.344).toFixed(2) + ' mi'
  return (meters / 1000).toFixed(2) + ' km'
}

function formatElev(meters, units) {
  const n = units === 'imperial' ? Math.round(meters * 3.28084) : Math.round(meters)
  const unit = units === 'imperial' ? 'ft' : 'm'
  return n.toLocaleString() + ' ' + unit
}

function formatArea(sqMeters, units) {
  if (units === 'imperial') return (sqMeters * 0.000247105).toFixed(2) + ' ac'
  return (sqMeters * 0.0001).toFixed(2) + ' ha'
}

function polylineLength(coords) {
  let total = 0
  for (let i = 1; i < coords.length; i++) total += haversineDistance(coords[i - 1], coords[i])
  return total
}

function ringArea(coords) {
  const R = 6371000
  let area = 0
  const n = coords.length - 1
  for (let i = 0; i < n; i++) {
    const [lng1, lat1] = coords[i]
    const [lng2, lat2] = coords[(i + 1) % n]
    area += (lng2 - lng1) * Math.PI / 180 *
      (2 + Math.sin(lat1 * Math.PI / 180) + Math.sin(lat2 * Math.PI / 180))
  }
  return Math.abs(area * R * R / 2)
}

function elevGain(map, coords) {
  // queryTerrainElevation is synchronous — reads already-loaded terrain tiles.
  // Returns null per-point when the tile isn't loaded; we fall back to N/A.
  try {
    const elevs = coords.map(c => map.queryTerrainElevation(c))
    if (elevs.some(e => e == null)) return null
    let gain = 0
    for (let i = 1; i < elevs.length; i++) {
      const d = elevs[i] - elevs[i - 1]
      if (d > 0) gain += d
    }
    return gain
  } catch { return null }
}

function getMetrics(type, shapeObj, units, map) {
  try {
    const geoJson = JSON.parse(shapeObj.geoJson)
    const feat = geoJson.features?.[0]
    if (!feat) return null

    if (type === 'circle') {
      const radius = feat.properties?.radius ?? 0
      return {
        label1: 'Radius', val1: formatLength(radius, units),
        label2: 'Area', val2: formatArea(Math.PI * radius * radius, units),
      }
    }
    if (type === 'poly') {
      const coords = feat.geometry?.coordinates?.[0] ?? []
      return {
        label1: 'Perimeter', val1: formatLength(polylineLength(coords), units),
        label2: 'Area', val2: formatArea(ringArea(coords), units),
      }
    }
    if (type === 'line') {
      const coords = feat.geometry?.coordinates ?? []
      const gain = map ? elevGain(map, coords) : null
      return {
        label1: 'Length', val1: formatLength(polylineLength(coords), units),
        label2: 'Elev. Gain', val2: gain !== null ? formatElev(gain, units) : 'N/A',
      }
    }
  } catch { /* empty */ }
  return null
}

let activeShapePopup = null

const measureTypeMap = { line: 'line', circle: 'circle', poly: 'polygon' }

export function openShapePopup(map, shapeObj, type, lngLat, onDelete) {
  if (activeShapePopup) activeShapePopup.remove()

  const popup = new maptilersdk.Popup({
    closeButton: false,
    maxWidth: '260px',
    className: 'bm-marker-popup',
    offset: 10,
  })

  const { userPreferences } = store.getState()
  const units = userPreferences?.units ?? 'metric'
  const formattedDate = dateConvertion(shapeObj.created)
  const { label1 = '', val1 = 'N/A', label2 = '', val2 = 'N/A' } = getMetrics(type, shapeObj, units, map) ?? {}

  const wrap = document.createElement('div')
  wrap.style.cssText = 'padding:10px;width:200px;background-color:black;visibility:visible;border-radius:10px;position:relative'
  wrap.innerHTML = `
    <div class="info-window-header-container">
      <div style="width:90%;overflow:hidden;white-space:nowrap;text-overflow:ellipsis;font-weight:bold;font-size:13px;color:#fff">${shapeObj.name ?? ''}</div>
      <div class="info-window-closeicon">
        <a id="iw-close" style="cursor:pointer">
          <img style="width:20px;object-fit:cover;float:right;margin-top:-10px" src="/asset/close.png" alt="Close"/>
        </a>
      </div>
    </div>
    <div style="position:relative;z-index:111">
      <h6 class="info-box-head-date">Created: <span class="info-box-head-date">${formattedDate ?? 'N/A'}</span></h6>
      <h6 class="info-box-head">${label1}: <span class="info-box-span">${val1}</span></h6>
      <h6 class="info-box-head">${label2}: <span class="info-box-span">${val2}</span></h6>
      <div style="display:flex;margin-top:10px">
        <div style="width:50px;height:55px;display:flex;flex-direction:column;align-items:center;justify-content:center">
          <a id="iw-edit" style="cursor:pointer">
            <div class="info-window-bottons">
              <img style="width:50%;height:50%;object-fit:cover" src="/asset/pencil.png" alt="Edit"/>
            </div>
          </a>
          <p>Edit</p>
        </div>
        <div style="width:50px;height:55px;display:flex;flex-direction:column;align-items:center;justify-content:center">
          <a id="iw-delete" style="cursor:pointer">
            <div class="info-window-bottons">
              <img style="width:50%;height:50%;object-fit:cover" src="/asset/bin.png"/>
            </div>
          </a>
          <p>Delete</p>
        </div>
      </div>
    </div>`

  wrap.querySelector('#iw-close').addEventListener('click', e => { e.stopPropagation(); popup.remove() })
  wrap.querySelector('#iw-edit').addEventListener('click', e => {
    e.stopPropagation()
    popup.remove()
    store.dispatch(expandLeftToolbar({
      dialog: 'edit',
      editable: { ...shapeObj, measureType: measureTypeMap[type] ?? type },
    }))
  })
  wrap.querySelector('#iw-delete').addEventListener('click', e => { e.stopPropagation(); onDelete() })

  popup.setDOMContent(wrap).setLngLat(lngLat).addTo(map)
  activeShapePopup = popup
  return popup
}
